// v2.11.0 — OPS tab : CRUD events Live.
// Table simple (active + soft-deleted) + modal create/edit.

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUB_CATS = ["sport", "festival", "degustation", "celebration", "anniversaire"];
const STATUSES = ["live_now", "today", "tomorrow", "upcoming"];

function _emptyForm() {
  return {
    title: "", sub_category: "festival", venue_name: "", commune: "Plateau",
    starts_at: "", ends_at: "", status: "upcoming",
    ticket_price_fcfa: "", description: "",
    lat: "", lng: "", image_override: "", whatsapp_contact: "",
  };
}

function _toIsoLocal(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 16); } catch (_) { return ""; }
}

export default function OpsLiveEventsTab({ token }) {
  const [events, setEvents] = useState([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState(null); // event object OR "new"
  const [form, setForm] = useState(_emptyForm());
  const [loading, setLoading] = useState(false);

  const headers = { "x-admin-token": token };

  const fetchAll = useCallback(() => {
    axios.get(`${API}/ops/live/events?include_deleted=${includeDeleted ? 1 : 0}`, { headers })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => toast.error("Erreur chargement events"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDeleted, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setForm(_emptyForm());
    setEditing("new");
  };
  const openEdit = (ev) => {
    setForm({
      title: ev.title || "", sub_category: ev.sub_category || "festival",
      venue_name: ev.venue_name || "", commune: ev.commune || "",
      starts_at: _toIsoLocal(ev.starts_at), ends_at: _toIsoLocal(ev.ends_at),
      status: ev.status || "upcoming",
      ticket_price_fcfa: ev.ticket_price_fcfa != null ? String(ev.ticket_price_fcfa) : "",
      description: ev.description || "",
      lat: ev.lat != null ? String(ev.lat) : "",
      lng: ev.lng != null ? String(ev.lng) : "",
      image_override: ev.image_override || "",
      whatsapp_contact: ev.whatsapp_contact || "",
    });
    setEditing(ev);
  };
  const closeModal = () => { setEditing(null); setForm(_emptyForm()); };

  const onSave = async () => {
    setLoading(true);
    const payload = {
      title: form.title.trim(),
      sub_category: form.sub_category,
      venue_name: form.venue_name.trim(),
      commune: form.commune.trim(),
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
      status: form.status,
      ticket_price_fcfa: form.ticket_price_fcfa ? Number(form.ticket_price_fcfa) : null,
      description: form.description.trim(),
      lat: form.lat ? Number(form.lat) : null,
      lng: form.lng ? Number(form.lng) : null,
      image_override: form.image_override.trim() || null,
      whatsapp_contact: form.whatsapp_contact.trim() || null,
    };
    if (!payload.title || !payload.sub_category) {
      toast.error("Titre + sous-catégorie obligatoires");
      setLoading(false); return;
    }
    try {
      if (editing === "new") {
        const r = await axios.post(`${API}/ops/live/events`, payload, { headers });
        toast.success(`✅ Event créé — ${r.data.event.address_civ || "sans adresse"}`);
      } else {
        const r = await axios.patch(`${API}/ops/live/events/${editing.id}`, payload, { headers });
        toast.success(`✅ Event mis à jour — ${r.data.event.address_civ || "sans adresse"}`);
      }
      closeModal();
      fetchAll();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.error || e.message}`);
    } finally { setLoading(false); }
  };

  const onDelete = async (ev) => {
    if (!window.confirm(`Soft-delete l'event "${ev.title}" ?`)) return;
    await axios.delete(`${API}/ops/live/events/${ev.id}`, { headers });
    toast.success("Event soft-deleted");
    fetchAll();
  };
  const onRestore = async (ev) => {
    await axios.post(`${API}/ops/live/events/${ev.id}/restore`, {}, { headers });
    toast.success("Event restored");
    fetchAll();
  };

  return (
    <div data-testid="ops-live-events-tab" style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>🎫 Events Live — {events.length} {includeDeleted ? "(actifs + soft-deleted)" : "actifs"}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="ops-include-deleted" /> Inclure soft-deleted
          </label>
          <button onClick={openCreate} data-testid="ops-event-create-btn" style={{
            background: "#16A34A", color: "#fff", border: "none",
            padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
          }}>+ Nouvel event</button>
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="ops-events-table">
        <thead>
          <tr style={{ background: "rgba(255,255,255,0.04)", textAlign: "left" }}>
            <th style={{ padding: 8 }}>Title</th>
            <th>Sub-cat</th>
            <th>Venue</th>
            <th>Address CIV</th>
            <th>Price</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => (
            <tr key={ev.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", opacity: ev.deleted_at ? 0.45 : 1 }} data-testid={`ops-event-row-${ev.id}`}>
              <td style={{ padding: 8 }}>{ev.title}{ev.deleted_at && <span style={{ color: "#ef4444", fontSize: 11, marginLeft: 6 }}>[deleted]</span>}</td>
              <td>{ev.sub_category}</td>
              <td>{ev.venue_name || "—"}</td>
              <td style={{ fontFamily: "monospace", fontSize: 11 }}>{ev.address_civ || "—"}</td>
              <td>{ev.ticket_price_fcfa ? `${ev.ticket_price_fcfa.toLocaleString("fr-FR")} F` : "Gratuit"}</td>
              <td>{ev.status}</td>
              <td style={{ display: "flex", gap: 4 }}>
                <button onClick={() => openEdit(ev)} data-testid={`ops-event-edit-${ev.id}`} style={{ background: "#3B82F6", color: "#fff", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Edit</button>
                {ev.deleted_at ? (
                  <button onClick={() => onRestore(ev)} data-testid={`ops-event-restore-${ev.id}`} style={{ background: "#16A34A", color: "#fff", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Restore</button>
                ) : (
                  <button onClick={() => onDelete(ev)} data-testid={`ops-event-delete-${ev.id}`} style={{ background: "#DC2626", color: "#fff", border: "none", padding: "4px 8px", borderRadius: 4, cursor: "pointer", fontSize: 11 }}>Delete</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div data-testid="ops-event-modal" onClick={closeModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: 24, maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto", color: "#fff" }}>
            <h3 style={{ marginTop: 0 }}>{editing === "new" ? "Nouvel event" : `Edit : ${editing.title}`}</h3>
            {[
              { k: "title", l: "Title *", type: "text" },
              { k: "sub_category", l: "Sub-category *", type: "select", opts: SUB_CATS },
              { k: "venue_name", l: "Venue name", type: "text" },
              { k: "commune", l: "Commune", type: "text" },
              { k: "starts_at", l: "Starts at", type: "datetime-local" },
              { k: "ends_at", l: "Ends at", type: "datetime-local" },
              { k: "status", l: "Status", type: "select", opts: STATUSES },
              { k: "ticket_price_fcfa", l: "Ticket price FCFA (0 = gratuit)", type: "number" },
              { k: "lat", l: "Latitude", type: "number" },
              { k: "lng", l: "Longitude", type: "number" },
              { k: "whatsapp_contact", l: "WhatsApp contact (+225...)", type: "text" },
              { k: "image_override", l: "Image override URL (optionnel)", type: "text" },
              { k: "description", l: "Description", type: "textarea" },
            ].map(({ k, l, type, opts }) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: "#94a3b8", display: "block", marginBottom: 3 }}>{l}</label>
                {type === "select" ? (
                  <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`ops-event-field-${k}`}
                    style={{ width: "100%", padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13 }}>
                    {opts.map((o) => <option key={o} value={o} style={{ background: "#0f172a" }}>{o}</option>)}
                  </select>
                ) : type === "textarea" ? (
                  <textarea value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`ops-event-field-${k}`}
                    style={{ width: "100%", padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13, minHeight: 60 }} />
                ) : (
                  <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`ops-event-field-${k}`}
                    style={{ width: "100%", padding: 6, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", borderRadius: 6, fontSize: 13 }} />
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={closeModal} style={{ flex: 1, background: "rgba(255,255,255,0.08)", color: "#cbd5e1", border: "1px solid rgba(255,255,255,0.12)", padding: "8px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Annuler</button>
              <button onClick={onSave} disabled={loading} data-testid="ops-event-save-btn" style={{ flex: 2, background: "#16A34A", color: "#fff", border: "none", padding: "8px", borderRadius: 6, cursor: loading ? "wait" : "pointer", fontWeight: 700, fontSize: 13 }}>
                {loading ? "…" : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
